import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import { Link } from '@gitbook/react-uikit';
import {
    Card,
    CardLink,
    Description,
    FieldError,
    FieldGroup,
    Heading,
    ImageIcon,
    Label,
    Skeleton,
    TextInput,
    TextSkeleton,
} from '@gitbook/spine-react';
import { useDebouncedFlush } from '@gitbook/util-react-hooks';
import { isHttpUrl } from '@gitbook/util-url';

import { apiQuery } from '../../state';
import { ImportModalFrame } from './ImportModalFrame';
import type { ImportModalProps } from './types';

/**
 * Prompt to import a website.
 */
export function WebsiteImportModal(props: ImportModalProps): React.ReactElement {
    const {
        title = 'Import a Website',
        subtitle = 'Enter a link to a web page below to import it into your space.',
        getSpace,
        startImport,
        importing,
    } = props;
    const [url, setURL] = React.useState('');
    const [isTouched, setIsTouched] = React.useState(false);

    const isValid = url ? isHttpUrl(url) : false;
    const [previewURL, setPreviewURL] = React.useState<string | null>(null);
    const debounce = useDebouncedFlush(500, { immediate: true });
    const updatePreviewURL = (url: string) => {
        debounce(() => {
            setPreviewURL(isHttpUrl(url) ? url : null);
        });
    };

    /** There's a delay to fetch the space during which we disable the modal actions. */
    const [submitting, setSubmitting] = React.useState(false);
    const onSubmit = async () => {
        setSubmitting(true);
        if (isValid) {
            const space = await getSpace();
            startImport([url], space);
        }
        setSubmitting(false);
    };

    return (
        <ImportModalFrame
            {...props}
            title={title}
            subtitle={subtitle}
            onSubmit={onSubmit}
            cannotSubmit={!url || !isValid}
            isLoading={submitting || importing}
        >
            <div className="flex flex-col gap-4">
                <FieldGroup>
                    <Label>URL</Label>
                    <Description>
                        We’ll crawl your site and pull in as much of your content as possible.
                    </Description>
                    <TextInput
                        aria-invalid={!isValid && isTouched ? true : undefined}
                        value={url}
                        onChange={(event) => {
                            setURL(event.target.value);
                            updatePreviewURL(event.target.value);
                            setIsTouched(true);
                        }}
                        autoFocus
                    />
                    <FieldError>{!isValid && isTouched ? 'URL is not valid' : ''}</FieldError>
                </FieldGroup>
                <Description>
                    GitBook has limits for imported content,{' '}
                    <Link url="https://gitbook.com/docs/help-center/editing-content/managing-your-content/is-there-a-limit-on-the-documentation-import-size-or-number">
                        read more here.
                    </Link>
                </Description>
                {previewURL ? (
                    <React.Suspense
                        fallback={
                            <Card className="mb-4">
                                <div className="flex items-center gap-4 p-4">
                                    <Skeleton shape="circle" className="size-10 shrink-0" />
                                    <div className="flex flex-col">
                                        <Heading>
                                            <TextSkeleton>Site title</TextSkeleton>
                                        </Heading>
                                        <Description>
                                            <TextSkeleton>Site description</TextSkeleton>
                                        </Description>
                                    </div>
                                </div>
                            </Card>
                        }
                    >
                        <WebsitePreview url={previewURL} />
                    </React.Suspense>
                ) : null}
            </div>
        </ImportModalFrame>
    );
}

/** Preview of a url */
function WebsitePreview(props: { url: string }) {
    const { url } = props;

    const { data: embed } = useSuspenseQuery(
        apiQuery('getEmbedByUrl', { url }, { nullOnError: true })
    );

    if (!embed) {
        return null;
    }

    return (
        <CardLink href={url} target="_blank" className="mb-4">
            <div className="flex items-center gap-4 p-4">
                {embed.icon ? (
                    <ImageIcon src={embed.icon} size={40} className="shrink-0" />
                ) : undefined}
                <div className="flex flex-col">
                    <Heading>{embed.title}</Heading>
                    {embed.site && <Description>{embed.site}</Description>}
                </div>
            </div>
        </CardLink>
    );
}
